import type { SweTaskRun } from "@/lib/api/types";
import { dashboardContent } from "@/lib/content";
import { fmtCompact, fmtSeconds } from "@/lib/dashboard/miner";
import { cn } from "@/lib/utils";

/**
 * Every attempt at one task. The runs are already in the aggregate, so expanding a row
 * costs nothing.
 *
 * There is no per-run baseline column: the backend sends no baseline on a run, and the
 * task's baseline is right above this table in the token cards.
 */
export function TaskRuns({ runs }: { runs: SweTaskRun[] }) {
  const t = dashboardContent.miner.tasks;
  // The column exists only where the competition reports JEV at all.
  const hasJev = runs.some((run) => typeof run.jev_input_tokens_with_compression === "number");
  if (runs.length === 0) return <p className="px-6 py-5 text-[14px] text-fg-muted">{t.noRuns}</p>;

  return (
    <table className="w-full border-collapse text-[13px]">
      <thead>
        <tr className="border-b border-line-soft">
          <Th align="center" className="w-12">
            {t.runColumns.n}
          </Th>
          <Th align="left">{t.runColumns.result}</Th>
          <Th>{t.runColumns.weighted}</Th>
          <Th>{t.runColumns.total}</Th>
          <Th>{t.runColumns.input}</Th>
          <Th>{t.runColumns.cache}</Th>
          <Th>{t.runColumns.output}</Th>
          {hasJev && <Th>{t.runColumns.jev}</Th>}
          <Th>{t.runColumns.time}</Th>
          <Th>{t.runColumns.steps}</Th>
        </tr>
      </thead>
      <tbody className="font-sans tabular-nums">
        {runs.map((run) => (
          <tr key={run.run_id} className="border-b border-line-soft last:border-0">
            <td className="py-2 text-center text-fg-faint">#{run.attempt_no}</td>
            <td className="py-2">
              {run.pass_with_compression === null ? (
                <span className="text-fg-faint">-</span>
              ) : (
                <span className={run.pass_with_compression ? "text-ok" : "text-accent"}>
                  {run.pass_with_compression ? `✓ ${t.pass}` : `✗ ${t.fail}`}
                </span>
              )}
            </td>
            <Num value={run.weighted_tokens_with_compression} />
            <Num value={run.tokens_with_compression} />
            <Num value={run.input_tokens_with_compression} />
            <Num value={run.cached_input_tokens_with_compression} />
            <Num value={run.output_tokens_with_compression} />
            {hasJev && <Num value={run.jev_input_tokens_with_compression} />}
            <td className="py-2 text-right text-fg-muted">{fmtSeconds(run.time_taken_seconds)}</td>
            <td className="py-2 text-right text-fg-muted">{run.agent_steps ?? "-"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * Alignment is always stated: a <th> centres by default, which left the "#" and "Result"
 * headings floating over columns whose cells sat on the left edge.
 */
function Th({
  children,
  align = "right",
  className,
}: {
  children: React.ReactNode;
  align?: "left" | "center" | "right";
  className?: string;
}) {
  return (
    <th
      scope="col"
      className={cn(
        "eyebrow pb-2 font-normal text-fg-faint",
        align === "left" ? "text-left" : align === "center" ? "text-center" : "text-right",
        className,
      )}
    >
      {children}
    </th>
  );
}

function Num({ value }: { value: number | null | undefined }) {
  return <td className="py-2 text-right">{fmtCompact(value)}</td>;
}
